var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _MockAdapter_instances, _MockAdapter_writeAnnotation;
import { accounts, annotations, bookings, projects, projectTasks, resourceCategories, resources, timeEntries, transactionCategories, workOrders, } from "./seed";
import { auditSubject } from "@/utils/audit";
import { durationMinutes } from "@/utils/duration";
import { compareText, sortRows } from "@/utils/sort";
import { assertStaffEdit, assertStaffResource, isStaffEntryEditable } from "@/utils/staffEntries";
import { staffAuditNotes } from "@/utils/staffAudit";
const sleep = (ms = 60) => new Promise((r) => setTimeout(r, ms));
const uuid = () => "id-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
const state = {
    accounts: [...accounts],
    workOrders: [...workOrders],
    projects: [...projects],
    projectTasks: [...projectTasks],
    transactionCategories: [...transactionCategories],
    resources: [...resources],
    resourceCategories: [...resourceCategories],
    bookings: [...bookings],
    timeEntries: [...timeEntries],
    annotations: [...annotations],
};
export class MockAdapter {
    constructor() {
        _MockAdapter_instances.add(this);
    }
    async listAccounts(activeOnly = false) {
        await sleep();
        let rows = [...state.accounts];
        if (activeOnly)
            rows = rows.filter((a) => a.statecode !== 1);
        return rows.sort((a, b) => compareText(a.name, b.name));
    }
    async listWorkOrders() {
        await sleep();
        return sortRows(state.workOrders, (w) => w.msdyn_name, "desc");
    }
    async listProjects() {
        await sleep();
        return sortRows(state.projects, (p) => p.msdyn_subject, "asc");
    }
    async listProjectTasks(projectId) {
        await sleep();
        return sortRows(state.projectTasks.filter((t) => t.msdyn_project === projectId), (t) => t.msdyn_subject, "asc");
    }
    async listTransactionCategories() {
        await sleep();
        return sortRows(state.transactionCategories, (c) => c.msdyn_description, "asc");
    }
    async listResources() {
        await sleep();
        return sortRows(state.resources, (r) => r.name, "asc");
    }
    async listResourceCategories() {
        await sleep();
        return sortRows(state.resourceCategories, (c) => c.name, "asc");
    }
    async listDirectReports(managerUserId) {
        const resources = await this.listResources();
        return resources.filter((resource) => managerUserId === "tim-user-id" && resource.userId && resource.userId !== managerUserId);
    }
    async listStaffTimeEntries(managerUserId) {
        const reports = await this.listDirectReports(managerUserId);
        const ids = new Set(reports.map((resource) => resource.bookableresourceid));
        return sortRows(state.timeEntries.filter((entry) => ids.has(entry.bookableresource) && isStaffEntryEditable(entry)), (entry) => Date.parse(entry.msdyn_start), "desc");
    }
    async createStaffTimeEntry(managerUserId, input) {
        const reports = await this.listDirectReports(managerUserId);
        assertStaffResource(reports, input.bookableresource);
        const created = await this.createTimeEntry({ ...input, msdyn_entryStatus: "Draft" });
        this.writeStaffAudit(managerUserId, reports, "Created on behalf", undefined, created);
        return created;
    }
    async updateStaffTimeEntry(managerUserId, id, patch) {
        const reports = await this.listDirectReports(managerUserId);
        const entry = state.timeEntries.find((row) => row.msdyn_timeentryid === id);
        if (!entry)
            throw new Error("This staff entry no longer exists. Refresh Staff Entries.");
        assertStaffEdit(reports, entry, patch);
        const updated = await this.updateTimeEntry(id, { ...patch, bookableresource: entry.bookableresource, msdyn_entryStatus: "Draft" });
        this.writeStaffAudit(managerUserId, reports, "Edited on behalf", entry, updated);
        return updated;
    }
    async submitStaffTimeEntry(managerUserId, id, resubmit = false) {
        const reports = await this.listDirectReports(managerUserId);
        const entry = state.timeEntries.find((row) => row.msdyn_timeentryid === id);
        if (!entry)
            throw new Error("This staff entry no longer exists. Refresh Staff Entries.");
        assertStaffEdit(reports, entry, {});
        if (entry.msdyn_entryStatus !== "Draft")
            throw new Error("Only Draft staff entries can be submitted. Refresh Staff Entries.");
        const before = { ...entry };
        await this.submitEntries([id]);
        this.writeStaffAudit(managerUserId, reports, resubmit ? "Resubmitted on behalf" : "Submitted on behalf", before, {
            ...entry, msdyn_entryStatus: "Submitted",
        });
    }
    writeStaffAudit(managerUserId, reports, subject, before, after) {
        const manager = state.resources.find((resource) => resource.userId === managerUserId);
        const employee = reports.find((resource) => resource.bookableresourceid === after.bookableresource);
        __classPrivateFieldGet(this, _MockAdapter_instances, "m", _MockAdapter_writeAnnotation).call(this, after.msdyn_timeentryid, subject, {
            actorName: manager?.name ?? managerUserId, actorUserId: managerUserId,
        }, staffAuditNotes(employee?.name ?? after.bookableresource, before, after));
    }
    async listBookings(workOrderId) {
        await sleep();
        let rows = [...state.bookings];
        if (workOrderId)
            rows = rows.filter((b) => b.workOrderId === workOrderId);
        return sortRows(rows, (b) => b.starttime ? Date.parse(b.starttime) : undefined, "desc");
    }
    async listTimeEntries(filter = {}) {
        await sleep();
        let rows = [...state.timeEntries];
        if (filter.resourceId)
            rows = rows.filter((r) => r.bookableresource === filter.resourceId);
        if (filter.status)
            rows = rows.filter((r) => r.msdyn_entryStatus === filter.status);
        if (filter.accountId) {
            rows = rows.filter((r) => clientIdFor(r) === filter.accountId);
        }
        if (filter.search) {
            const q = filter.search.toLowerCase();
            rows = rows.filter((r) => r.msdyn_description.toLowerCase().includes(q) ||
                r.regardingobjectid?.display.toLowerCase().includes(q));
        }
        if (filter.from) {
            const f = +new Date(filter.from);
            rows = rows.filter((r) => +new Date(r.msdyn_start) >= f);
        }
        if (filter.to) {
            const t = +new Date(filter.to);
            rows = rows.filter((r) => +new Date(r.msdyn_start) <= t);
        }
        return rows.sort((a, b) => +new Date(b.msdyn_start) - +new Date(a.msdyn_start));
    }
    async createTimeEntry(input) {
        await sleep();
        const dur = durationMinutes(input);
        // Mirror Dataverse: msdyn_timesource is auto-derived from regarding —
        // Work Order → FieldService, anything else → ProjectService.
        const timesource = input.regardingobjectid?.type === "msdyn_workorder" ? "FieldService" : "ProjectService";
        const row = {
            ...input,
            msdyn_timeentryid: uuid(),
            msdyn_duration: dur,
            msdyn_timesource: timesource,
        };
        state.timeEntries.unshift(row);
        return row;
    }
    async updateTimeEntry(id, patch) {
        await sleep();
        const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
        if (idx === -1)
            throw new Error(`Time entry ${id} not found`);
        // Defense in depth: UI hides Edit/Save on Approved rows, but adapters refuse
        // the write too so future code paths can't silently mutate locked entries.
        // Approver-driven status changes go through approveEntries / returnEntries.
        if (state.timeEntries[idx].msdyn_entryStatus === "Approved") {
            throw new Error("Approved time entries are read-only. Ask your approver to return the entry first.");
        }
        const next = { ...state.timeEntries[idx], ...patch };
        if (patch.msdyn_start || patch.msdyn_end) {
            next.msdyn_duration = durationMinutes({
                msdyn_start: next.msdyn_start,
                msdyn_end: next.msdyn_end,
                msdyn_duration: patch.msdyn_duration,
            });
        }
        // Re-derive timesource if regarding changed (parity with DataverseAdapter).
        if (patch.regardingobjectid) {
            next.msdyn_timesource =
                patch.regardingobjectid.type === "msdyn_workorder" ? "FieldService" : "ProjectService";
        }
        state.timeEntries[idx] = next;
        return next;
    }
    async deleteTimeEntry(id) {
        await sleep();
        const row = state.timeEntries.find((t) => t.msdyn_timeentryid === id);
        if (row && row.msdyn_entryStatus === "Approved") {
            throw new Error("Approved time entries cannot be deleted.");
        }
        state.timeEntries = state.timeEntries.filter((t) => t.msdyn_timeentryid !== id);
    }
    async markWhmcsPosted(id, billableId, postedOn) {
        await sleep();
        const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
        if (idx === -1)
            throw new Error(`Time entry ${id} not found`);
        const next = {
            ...state.timeEntries[idx],
            crb7a_whmcsposted: true,
            crb7a_whmcsbillableitemid: billableId,
            crb7a_whmcspostedon: postedOn,
        };
        state.timeEntries[idx] = next;
        return next;
    }
    async approveEntries(ids, audit) {
        await sleep();
        const subject = auditSubject("approved", audit.delegationRole);
        for (const id of ids) {
            const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
            if (idx !== -1)
                state.timeEntries[idx].msdyn_entryStatus = "Approved";
            __classPrivateFieldGet(this, _MockAdapter_instances, "m", _MockAdapter_writeAnnotation).call(this, id, subject, audit, audit.comment ?? "");
        }
    }
    async returnEntries(ids, audit, comment) {
        await sleep();
        // v0.6.1.2 — WHMCS-posted gate. Mirrors the DataverseAdapter pre-check
        // so behaviour is identical regardless of backend.
        assertNoneWhmcsPostedMock(ids);
        const subject = auditSubject("returned", audit.delegationRole);
        for (const id of ids) {
            const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
            if (idx !== -1)
                state.timeEntries[idx].msdyn_entryStatus = "Returned";
            __classPrivateFieldGet(this, _MockAdapter_instances, "m", _MockAdapter_writeAnnotation).call(this, id, subject, audit, comment);
        }
    }
    async selfReturnEntries(ids, audit) {
        await sleep();
        assertNoneWhmcsPostedMock(ids);
        const subject = auditSubject("self-returned");
        for (const id of ids) {
            const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
            if (idx !== -1)
                state.timeEntries[idx].msdyn_entryStatus = "Returned";
            __classPrivateFieldGet(this, _MockAdapter_instances, "m", _MockAdapter_writeAnnotation).call(this, id, subject, audit, audit.comment ?? "");
        }
    }
    async selfApproveEntries(ids, audit) {
        await sleep();
        const subject = auditSubject("self-approved");
        for (const id of ids) {
            const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
            if (idx !== -1)
                state.timeEntries[idx].msdyn_entryStatus = "Approved";
            __classPrivateFieldGet(this, _MockAdapter_instances, "m", _MockAdapter_writeAnnotation).call(this, id, subject, audit, audit.comment ?? "");
        }
    }
    async submitEntries(ids) {
        await sleep();
        for (const id of ids) {
            const idx = state.timeEntries.findIndex((t) => t.msdyn_timeentryid === id);
            if (idx !== -1)
                state.timeEntries[idx].msdyn_entryStatus = "Submitted";
        }
    }
    async listAnnotations(timeEntryId) {
        await sleep();
        return state.annotations
            .filter((a) => a.objectid === timeEntryId)
            .sort((a, b) => +new Date(b.createdon) - +new Date(a.createdon));
    }
    async listPendingForManager(managerUserId) {
        await sleep();
        // Mock manager map: everyone except Tim reports to Tim.
        // Tim has no manager (drives self-approve tab).
        const reports = state.resources
            .filter((r) => r.userId && r.userId !== "tim-user-id")
            .map((r) => r.bookableresourceid);
        return state.timeEntries
            .filter((t) => t.msdyn_entryStatus === "Submitted" &&
            managerUserId === "tim-user-id" &&
            reports.includes(t.bookableresource))
            .sort((a, b) => +new Date(b.msdyn_start) - +new Date(a.msdyn_start));
    }
    async listPendingForSelf(userId) {
        await sleep();
        const ownResourceIds = state.resources
            .filter((r) => r.userId === userId)
            .map((r) => r.bookableresourceid);
        return state.timeEntries.filter((t) => t.msdyn_entryStatus === "Submitted" && ownResourceIds.includes(t.bookableresource)).sort((a, b) => Date.parse(b.msdyn_start) - Date.parse(a.msdyn_start));
    }
    async listPendingDelegated(scope) {
        await sleep();
        const pending = state.timeEntries
            .filter((t) => t.msdyn_entryStatus === "Submitted")
            .sort((a, b) => Date.parse(b.msdyn_start) - Date.parse(a.msdyn_start));
        switch (scope.role) {
            case "Admin":
            case "Finance.Approver":
                return pending;
            case "Operations.Manager": {
                const inBu = state.resources
                    .filter((r) => r.businessUnitId === scope.businessUnitId)
                    .map((r) => r.bookableresourceid);
                return pending.filter((t) => inBu.includes(t.bookableresource));
            }
            case "Project.Manager": {
                const myProjectIds = state.projects
                    .filter((p) => p.msdyn_projectmanager === scope.managerUserId)
                    .map((p) => p.msdyn_projectid);
                return pending.filter((t) => t.regardingobjectid?.type === "msdyn_project" &&
                    myProjectIds.includes(t.regardingobjectid.id));
            }
        }
    }
}
_MockAdapter_instances = new WeakSet(), _MockAdapter_writeAnnotation = function _MockAdapter_writeAnnotation(objectid, subject, audit, notetext) {
    state.annotations.unshift({
        annotationid: uuid(),
        objectid,
        objecttypecode: "msdyn_timeentry",
        subject,
        notetext,
        createdon: new Date().toISOString(),
        createdbyName: audit.actorName,
    });
};
/**
 * v0.6.1.2 — Mirror of DataverseAdapter's WHMCS-posted guard. Refuses the
 * return operation outright if any candidate has already been posted to
 * WHMCS, so the mock backend can rehearse the same all-or-nothing failure
 * mode the real backend will throw.
 */
function assertNoneWhmcsPostedMock(ids) {
    const posted = ids.filter((id) => {
        const row = state.timeEntries.find((t) => t.msdyn_timeentryid === id);
        return row?.crb7a_whmcsposted === true;
    });
    if (posted.length > 0) {
        throw new Error(posted.length === ids.length
            ? "These entries have already been posted to WHMCS and can't be returned. Reverse the billing item in WHMCS first."
            : `${posted.length} of ${ids.length} entries have been posted to WHMCS and can't be returned. Reverse the billing item in WHMCS first.`);
    }
}
/** Resolve regardingobjectid → owning account id (used for billing filters). */
function clientIdFor(entry) {
    const r = entry.regardingobjectid;
    if (!r)
        return;
    if (r.type === "account")
        return r.id;
    if (r.type === "msdyn_workorder") {
        return state.workOrders.find((w) => w.msdyn_workorderid === r.id)?.msdyn_serviceaccount;
    }
    if (r.type === "msdyn_project") {
        return state.projects.find((p) => p.msdyn_projectid === r.id)?.msdyn_customer;
    }
}
